import browser from 'browser-detect';
import { Title } from '@angular/platform-browser';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { ActivationEnd, Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { UserService } from './core/services/UserService';
import { AutoLogoutService } from "./core/services/AutoLogoutService";
import { IOrganization } from './core/interfaces/organization/IOrganization';
import { OrganizationModalComponent } from './static/organization/organization-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  ActionAuthLogin,
  ActionAuthLogout,
  AnimationsService,
  selectorAuth,
  routeAnimations
} from '@app/core';
import { environment as env } from '@env/environment';


import {
  NIGHT_MODE_THEME,
  selectorSettings,
  SettingsState,
  ActionSettingsChangeAnimationsPageDisabled
} from './settings';
import { AuthenticationService } from './core/services/AuthenticationService';


import {
  ActionSettingsChangeTheme,
  ActionSettingsPersist,
} from './settings/settings.reducer';
import { TaskCenterNotificationService } from './core/services/TaskCenterNotificationService';


@Component({
  standalone: false,
  selector: 'anms-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routeAnimations]
})
export class AppComponent implements OnInit, OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();
  @HostBinding('class') componentCssClass;
  isProd = env.production;
  envName = env.envName;
  version = env.versions.app;
  year = new Date().getFullYear();
  logo = env.logoUrl || 'assets/logo.png';
  navigation = [];
  userUpdatesSubscription: Subscription;
  navigationSideMenu = [];
  isAuthenticated;
  navigtionMenu = [];
  public appId: number = env.appId;
  public showChatBot: boolean = false;
  chatConversation: any = null;
  isAdmin: boolean = false;
  isCompanyAdmin: boolean = false;
  pendingEmployerCertificateCount = 0;
  isStoppingImpersonation = false;
  constructor(
    public overlayContainer: OverlayContainer,
    private store: Store<any>,
    private router: Router,
    private titleService: Title,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public autoLogoutService: AutoLogoutService,
    private animationService: AnimationsService,
    private taskCenterNotificationService: TaskCenterNotificationService
  ) {
    this.userUpdatesSubscription = this.authenticationService.onUserUpdate().subscribe(message => { this.userUpdated(message); });
    this.autoLogoutService.initListener();

    this.changeTheme({ value: env.themeName });
    this.isAdmin = this.userService.userHasRole('admin');
    this.isCompanyAdmin = this.isSamekonomiOrganizationAdmin();

  }

  ngOnInit(): void {
    this.subscribeToSettings();
    this.subscribeToIsAuthenticated();
    this.subscribeToRouterEvents();
    this.subscribeToTaskCenterNotifications();
    this.setMenu();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  
  setMenu()
  {
    this.isAdmin = this.userService.userHasRole('admin');
    this.isCompanyAdmin = this.isSamekonomiOrganizationAdmin();
    this.updateTaskCenterPendingCount();

    //ADMIN
    if (this.appId != 2 && this.userService.userHasRole('admin')) {

      //Hamburger navigation button
      this.navigtionMenu = [
        { link: 'admin/task-center', label: 'Utbetalningar', icon: 'pending_actions', title: 'Utbetalningar' },
        { link: 'transactions', label: 'Ekonomi', icon: 'attach_money', title: 'Ekonomi' },
        //{ link: 'admin/documents', label: 'Medlemsdokument', icon: 'folder_shared', title: 'Medlemsdokument' },
        { link: 'admin/reports', label: 'Medlemsrapporter', icon: 'fact_check', title: 'Medlemsrapporter' },
        { link: 'settings', label: 'Inställningar', icon: 'settings', title: 'Mina inställningar' },
        { link: 'mypages/customers', label: 'Kunder', icon: 'people_alt', title: 'Kunder' },
        { link: 'account-balance', label: 'Lönesaldo', icon: 'chat', title: 'Lönesaldo' },
        { link: 'mypages/mydocuments', label: 'Mina dokument', icon: 'attachment', title: 'Mina dokument' },
        { link: 'admin/discount-codes', label: 'Rabattkoder', icon: 'sell', title: 'Rabattkoder' },
        { link: 'admin/supportchat', label: 'Supportchat', icon: 'chat', title: 'Supportchat' },
        { link: 'admin/payout-reports', label: 'Återrapportering', icon: 'autorenew', title: 'Återrapportering' }
      ];

      //Main navigation
      this.navigation = [
        { link: 'mypages/invoices', label: 'Fakturor', icon: 'list_alt', title: 'Fakturor' },
        { link: 'admin/task-center', label: 'Utbetalningar', icon: 'pending_actions', title: 'Utbetalningar' },
        //{ link: 'admin/admin-dashboard', label: 'Admin', icon: 'fireplace', title: 'Admin' },
        //{ link: 'admin/invoices-admin', label: 'Fakturor', icon: 'format_list_bulleted_add', title: 'Fakturor-admin' },
        //{ link: 'combined-invoice-tool', label: 'Ny samlingsfaktura', icon: 'library_books', title: 'Ny samlingsfaktura' },
        { link: 'admin/organizations', label: 'Organisationer', icon: 'group', title: 'Organisationer' },
        //{ link: 'admin/discount-codes', label: 'Rabattkoder:', icon: 'sell', title: 'Rabattkoder:' },
        { link: 'users', label: 'Användare', icon: 'person', title: 'Användare' },
        { link: 'admin/documents', label: 'Medlemsdokument', icon: 'folder_shared', title: 'Medlemsdokument' }
        //{ link: 'about', label: 'Hem', icon: 'home', title: 'Hem' },
        //{ link: 'settings', label: 'Inställningar', icon: 'settings', title: 'Inställningar' },
      ];
      this.navigation.push({ link: 'organization/ipsos-import', label: 'IPSOS filimport', icon: 'upload_file', title: 'IPSOS filimport' });
      this.navigation.push({ link: 'admin/kivra', label: 'Kivra', icon: 'qr_code', title: 'Kivra' });

      //Mobile navigation
      this.navigationSideMenu = [
        { link: 'mypages/invoices', label: 'Fakturor', icon: 'list_alt', title: 'Fakturor' },
        { link: 'admin/task-center', label: 'Utbetalningar', icon: 'pending_actions', title: 'Utbetalningar' },
        { link: 'users', label: 'Användare', icon: 'people_alt', title: 'Användare' },
        { link: 'transactions', label: 'Ekonomi', icon: 'attach_money', title: 'Ekonomi' },
        { link: 'payout-reports', label: 'Återrapportering', icon: 'autorenew', title: 'Återrapportering' },
        { link: 'admin/documents', label: 'Medlemsdokument', icon: 'folder_shared', title: 'Dokument' },
        { link: 'mypages/customers', label: 'Kunder', icon: 'people_alt', title: 'Kunder' },
        { link: 'about', label: 'Hem', icon: 'home', title: 'Hem' },
        { link: 'settings', label: 'Inställningar', icon: 'settings', title: 'Inställningar' }
      ];
      this.navigationSideMenu.push({ link: 'organization/ipsos-import', label: 'IPSOS filimport', icon: 'upload_file', title: 'IPSOS filimport' });
      this.navigationSideMenu.push({ link: 'admin/kivra', label: 'Kivra', icon: 'qr_code', title: 'Kivra' });
    }
    //SAMekonomi member
    else if (this.appId == 2) {

        //Hamburger navigation button
        this.navigtionMenu = [
          { link: 'settings', label: 'Mina inställningar', icon: 'settings_applications', title: 'Mina inställningar' }
      ];
      
      if (this.userService.userHasRole('companyuser') && this.userService.userIsParentAccount()) {
        this.navigtionMenu.push({ link: 'users', label: 'Användare', icon: 'people_alt', title: 'Användare' });
      }

      //Main navigation
      this.navigation = [
        { link: 'about', label: 'Hem', icon: 'home', title: 'Hem' },
        { link: 'combined-invoice-tool', label: 'Ny samlingsfaktura', icon: 'library_books', title: 'Ny faktura' },
        { link: 'organization/invoices', label: 'Fakturor', icon: 'list_alt', title: 'Fakturor' },
        { link: 'mypages/customers', label: 'Kunder', icon: 'storefront', title: 'Kunder' },
      ];
      if (this.canAccessIpsosImport()) {
        this.navigation.push({ link: 'organization/ipsos-import', label: 'IPSOS filimport', icon: 'upload_file', title: 'IPSOS filimport' });
      }
      //Mobile navigation
      this.navigationSideMenu = [
        { link: 'combined-invoice-tool', label: 'Ny samlingsfaktura', icon: 'library_books' },
        { link: 'organization/invoices', label: 'Fakturor', icon: 'list_alt' },
        { link: 'mypages/customers', label: 'Kunder', icon: 'people_alt' },
        { link: 'mypages/mydocuments', label: 'Mina dokument', icon: 'attachment', title: 'Mina dokument' },
        { link: 'contact', label: 'Kontakt', icon: 'perm_contact_calendar' },
        { link: 'about', label: 'Hem', icon: 'home' },
        { link: 'settings', label: 'Inställningar', icon: 'settings' }
      ];
      if (this.canAccessIpsosImport()) {
        this.navigationSideMenu.push({ link: 'organization/ipsos-import', label: 'IPSOS filimport', icon: 'upload_file', title: 'IPSOS filimport' });
      }
      }
    //SAMpoolen  member
    else if ((!this.userService.userHasRole('admin')) && this.appId == 1) {
        //Hamburger navigation button
          this.navigtionMenu = [
            { link: 'contact', label: 'Kontakt', icon: 'perm_contact_calendar', title: 'Kontakt' },
            { link: 'account-balance', label: 'Lönesaldo', icon: 'chat', title: 'Lönesaldo' },
              { link: 'settings', label: 'Inställningar', icon: 'settings', title: 'Inställningar' },
              { link: 'mypages/customers', label: 'Kunder', icon: 'people_alt', title: 'Kunder' },
              { link: 'job-calendar', label: 'Jobbkalendern', icon: 'event_note', title: 'Jobbkalendern' }
          ];

        //Main navigation
          this.navigation = [
            { link: 'invoice-tool', label: 'Ny faktura', icon: 'description', title: 'Ny faktura' },
            { link: 'mypages/invoices', label: 'Fakturor', icon: 'list_alt', title: 'Fakturor' },
            { link: 'transactions', label: 'Ekonomi', icon: 'attach_money', title: 'Ekonomi' },
            { link: 'mypages/mydocuments', label: 'Mina dokument', icon: 'attachment', title: 'Mina dokument' },
              { link: 'about', label: 'Hem', icon: 'home', title: 'Hem' }
          ];
        //Mobile navigation
          this.navigationSideMenu = [
            { link: 'invoice-tool', label: 'Ny faktura', icon: 'description' },
            { link: 'job-calendar', label: 'Jobbkalendern', icon: 'event_note' },
            { link: 'mypages/invoices', label: 'Fakturor', icon: 'list_alt' },
            { link: 'mypages/customers', label: 'Kunder', icon: 'people_alt' },
            { link: 'transactions', label: 'Ekonomi', icon: 'attach_money' },
            { link: 'mypages/mydocuments', label: 'Mina dokument', icon: 'attachment', title: 'Mina dokument' },
            { link: 'contact', label: 'Kontakt', icon: 'perm_contact_calendar' },
            { link: 'about', label: 'Hem', icon: 'home' },
            { link: 'settings', label: 'Mina inställningar', icon: 'settings' }
      ];
    }
    if (this.isCompanyAdmin && this.authenticationService.getCurrentUserOrganization()?.IsApiCustomer === true) {
      this.navigation.push({ link: 'organization/api-accounts', label: 'API-konton', icon: 'vpn_key', title: 'API-konton' });
      this.navigationSideMenu.push({ link: 'organization/api-accounts', label: 'API-konton', icon: 'vpn_key', title: 'API-konton' });
    }
  }

  changeTheme({ value: theme }) {
    this.store.dispatch(new ActionSettingsChangeTheme({ theme }));
  }

  userUpdated(user: any) {
    this.setMenu();
  }

  private subscribeToTaskCenterNotifications(): void {
    this.taskCenterNotificationService.pendingCount$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(count => this.pendingEmployerCertificateCount = count);
  }

  private updateTaskCenterPendingCount(): void {
    if (this.isAdmin) {
      this.taskCenterNotificationService.refreshPendingCount();
      return;
    }

    this.taskCenterNotificationService.reset();
  }

  private isIpsosOrganization(): boolean {
    const organization = this.authenticationService.getCurrentUserOrganization();
    const orgNumber = organization && organization.Company ? organization.Company.OrgNumber : '';
    return String(orgNumber || '').replace(/\D/g, '') === '5566246954';
  }

  private canAccessIpsosImport(): boolean {
    return this.isAdmin || this.isCompanyAdmin && this.isIpsosOrganization();
  }

  private isSamekonomiOrganizationAdmin(): boolean {
    return this.userService.userHasRole('companyadmin') ||
      this.appId == 2 && this.userService.isOrganizationMasterAccount();
  }

  openOrganizationModal() {
    
    var organization = this.authenticationService.getCurrentUserOrganization();

    const dialogRef = this.dialog.open(OrganizationModalComponent, {
      width: '90%',
      height: '90%',
      data: {
        organization: organization
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      //this.createOrganization(result.Id);
      //this.getOrganizations();
    });
  }

  onCreateAccountClick()
  {
      this.router.navigate(['/create-account']);
  }

  onLogoutClick() {
      this.authenticationService.logout();
  }

  get impersonationState(): any {
    return this.authenticationService.getImpersonationState();
  }

  get isModernLayoutRoute(): boolean {
    return !!this.isAuthenticated;
  }

  stopImpersonation(): void {
    if (this.isStoppingImpersonation) {
      return;
    }

    this.isStoppingImpersonation = true;
    this.authenticationService.stopImpersonation().subscribe({
      next: () => {
        this.isStoppingImpersonation = false;
        this.router.navigate(['/users']);
      },
      error: error => {
        this.isStoppingImpersonation = false;
        const message = error?.error?.detail || 'Det gick inte att återgå till administratörskontot. Logga in igen.';
        this.snackBar.open(message, '', { duration: 7000 });
      }
    });
  }

  private subscribeToIsAuthenticated() {

      this.store
      .select(selectorAuth)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(auth => (this.authenticated(auth)));
  }

  authenticated(auth: any)
  {
      this.isAuthenticated = auth.isAuthenticated;
  }

  private subscribeToSettings() {
    if (AppComponent.isIEorEdge()) {
      this.store.dispatch(
        new ActionSettingsChangeAnimationsPageDisabled({
          pageAnimationsDisabled: true
        })
      );
    }
    this.store
      .select(selectorSettings)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(settings => {
        this.setTheme(settings);
        this.animationService.updateRouteAnimationType(
          settings.pageAnimations,
          settings.elementsAnimations
        );
      });
  }

  private setTheme(settings: SettingsState) {
    const { theme, autoNightMode } = settings;
    const hours = new Date().getHours();
    const effectiveTheme = (autoNightMode && (hours >= 20 || hours <= 6)
      ? NIGHT_MODE_THEME
      : theme
    ).toLowerCase();
    this.componentCssClass = effectiveTheme;
    const classList = this.overlayContainer.getContainerElement().classList;
    const toRemove = Array.from(classList).filter((item: string) =>
      item.includes('-theme')
    );
    if (toRemove.length) {
      classList.remove(...toRemove);
    }
    classList.add(effectiveTheme);
  }

  private subscribeToRouterEvents() {
    this.router.events
      .pipe(
        filter(
          event =>
            event instanceof ActivationEnd || event instanceof NavigationEnd
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(event => {
        if (event instanceof ActivationEnd) {
          this.setPageTitle(event);
        }

        if (event instanceof NavigationEnd) {
          AppComponent.trackPageView(event);
        }
      });
  }

  private setPageTitle(event: ActivationEnd) {
    let lastChild = event.snapshot;
    while (lastChild.children.length) {
      lastChild = lastChild.children[0];
    }
    const { title } = lastChild.data;
    this.titleService.setTitle(
      title ? `${title} - ${env.appName}` : env.appName
    );
  }

  openChatBot(conversation: any = null) {
    this.chatConversation = conversation;
    this.showChatBot = true;
  }

  closeChatBot() {
    this.chatConversation = null;
    this.showChatBot = false;
  }


  onChatBotClose(event) {
    this.showChatBot = false;
  }

  private static trackPageView(event: NavigationEnd) {
      (<any>window).ga('set', 'page', event.urlAfterRedirects);
      (<any>window).ga('send', 'pageview');
  }

  private static isIEorEdge() {
      return ['ie', 'edge'].includes(browser().name);
  }

  onLoginClick() {
      this.router.navigate(['/login']);
  }

}
